<template>
  <v-dialog
    v-model="dialogState"
    max-width="1080"
  >
    <v-card class="pa-3">
      <v-card-title>
        <v-icon class="mr-2">
          fmd_bad
        </v-icon> Adverse Individuals
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="adverseIndividuals"
        sort-by="first_name"
        class="elevation-1 rounded"
        hide-default-footer
        disable-pagination
      >
        <template #[`item.a_number`]="{ item }">
          {{ aNumberFormatDisplay(item.a_number) }}
        </template>
        <template #[`item.ssn`]="{ item }">
          {{ ssnFormatDisplay(item.ssn) }}
        </template>
        <template #[`item.edit`]="{ item }">
          <v-icon
            style="cursor: pointer;"
            @click="() => editAdverseIndividual(item)"
          >
            edit
          </v-icon>
        </template>
        <template #[`item.remove`]="{ item }">
          <v-icon
            style="cursor: pointer;"
            @click="() => removeAdverseIndividual(item)"
          >
            delete
          </v-icon>
        </template>
      </v-data-table>

      <template v-if="showForm">
        <v-form
          ref="dialogForm"
          v-model="allInputValid"
          autocomplete="off"
        >
          <v-container>
            <h4 class="mt-4">
              {{ isCreate ? 'Add' : 'Edit' }} Adverse Individual
            </h4>
            <v-row>
              <!-- if recordView (or !adverseTo), could add dropdown of individuals, and make it required -->

              <!-- later? add support for entering record id? or auto-populating it? -->

              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="adverseIndividual.first_name"
                  filled
                  label="First Name"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="adverseIndividual.last_name"
                  filled
                  label="Last Name"
                />
              </v-col>
              <BasicDateField
                :date="adverseIndividual.dob"
                label="DOB"
                @update-date="updateDobField"
                @valid-changed="updateDatesValid"
              />
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="adverseIndividual.a_number"
                  v-mask="aNumMask"
                  filled
                  label="A#"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="adverseIndividual.ssn"
                  v-mask="ssnMask"
                  filled
                  label="SSN"
                />
              </v-col>
              <v-col
                cols="12"
                sm="12"
              >
                <v-textarea
                  v-model="adverseIndividual.notes"
                  filled
                  rows="1"
                  auto-grow
                  label="Notes"
                />
              </v-col>
            </v-row>

          <!-- <small>* indicates required field</small> -->
          </v-container>

          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              color="secondary"
              @click="dialogState = false"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              type="submit"
              :disabled="!allInputValid"
              @click="saveAdverseIndividual"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-form>
      </template>

      <template v-if="!viewOnly && !showForm">
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            @click="addAdverseIndividual"
          >
            Add
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { cloneDeep } from 'lodash'

import { MASKS, aNumberFormatDisplay, ssnFormatDisplay } from '@/utilities'
import BasicDateField from '@/components/Fields/BasicDate.vue'
import { alertHandler } from '@/alertHandler'
import { axiosRequest } from '@/plugins/axios-common'

const UNPERMITTED_PARAMS = [
  'created_at',
  'updated_at',
  'id',
]

export default {
  name: 'DialogAdverseIndividual',

  components: {
    BasicDateField,
  },

  props: {
    adverseIndividualData: {
      type: Array,
      default: _ => ([]),
    },

    adverseTo: {
      type: Number,
      default: 0,
    },

    recordView: {
      type: Boolean,
      default: false,
    },

    viewOnly: {
      type: Boolean,
      default: true,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      aNumMask: MASKS.ANUMBER,
      aNumberFormatDisplay: aNumberFormatDisplay,
      adverseIndividual: {},
      adverseIndividuals: [],
      headers: [
        { text: 'First Name', value: 'first_name' },
        { text: 'Last Name', value: 'last_name' },
        { text: 'DOB', value: 'dob' },
        { text: 'A#', value: 'a_number' },
        { text: 'SSN', value: 'ssn' },
        { text: 'Notes', value: 'notes' },
      ],

      isCreate: false,
      rules: {
        required: [
          v => !!v || 'Required',
        ],
      },

      showForm: false,
      ssnMask: MASKS.SSNUMBER,
      valid: false,
      validDates: true,
    }
  },

  computed: {
    allInputValid: {
      get () {
        return this.valid && this.validDates
      },

      set (newVal) {
        this.valid = newVal
      },
    },

    dialogState: {
      get () {
        return this.$store.state.dialogOpenStates.viewAdverseIndividuals
      },

      set (newValue) {
        if (newValue === this.dialogState) return
        this.setViewAdverseIndividualsDialog(newValue)
      },
    },

    ...mapGetters([
      'currentUser',
    ]),
  },

  watch: {
    adverseIndividualData () {
      // This is necessary when dialog is shown in Record view
      this.adverseIndividuals = this.adverseIndividualData
    },

    dialogState () {
      this.showForm = false
      this.adverseIndividual = {}
    },
  },

  created () {
    this.adverseIndividuals = this.adverseIndividualData
    if (!this.viewOnly) {
      this.headers.push({
        sortable: false,
        text: 'Edit',
        value: 'edit',
      })

      if (this.currentUser && this.currentUser.isAdmin) {
        this.headers.push({
          sortable: false,
          text: 'Remove',
          value: 'remove',
        })
      }
    }

    if (this.recordView) {
      this.headers.push({
        text: 'Adverse To',
        value: 'party_name',
      })
    }
  },

  methods: {
    addAdverseIndividual () {
      this.isCreate = true
      this.showForm = true
    },

    editAdverseIndividual (adverse) {
      this.isCreate = false
      this.adverseIndividual = adverse
      this.showForm = true
    },

    handleError (error) {
      this.displayAlert('error', 'There was a problem saving the adverse individual', error)
    },

    removeAdverseIndividual (adverse) {
      const result = confirm('Are you sure you want to delete this adverse individual? This cannot be undone.')
      if (result) {
        axiosRequest.
          delete(`/adverse_individuals/${adverse.id}`).
          then(() => {
            const index = this.adverseIndividuals.findIndex(ap => { return ap.id === adverse.id })
            if (index > -1) this.adverseIndividuals.splice(index, 1)
            this.displayAlert('success', 'Adverse individual removed')
          }).
          catch(this.handleError)
      }
    },

    saveAdverseIndividual (e) {
      e.preventDefault()

      const data = cloneDeep(this.adverseIndividual)
      const id = this.adverseIndividual.id
      UNPERMITTED_PARAMS.forEach(unpermitted => {
        delete data[unpermitted]
      })

      // need to strip hyphens out of masked a_number
      if (data.a_number) data.a_number = data.a_number.replace(/[-]/g, '')
      if (data.ssn) data.ssn = data.ssn.replace(/-/g, '')

      if (this.isCreate) {
        // set the party_id to the adverseTo prop
        data.party_id = this.adverseTo
        axiosRequest.
          post('/adverse_individuals', {
            adverse_individual: data,
          }).
          then(response => {
            this.adverseIndividuals.push(response.data.adverse_individual)
            this.setViewAdverseIndividualsDialog(false)
            this.displayAlert('success', 'Adverse individual saved')
          }).
          catch(this.handleError)
      } else {
        axiosRequest.
          put(`/adverse_individuals/${id}`, {
            adverse_individual: data,
          }).
          then(() => {
            this.setViewAdverseIndividualsDialog(false)
            this.displayAlert('success', 'Adverse individual saved')
          }).
          catch(this.handleError)
      }
    },

    ssnFormatDisplay: ssnFormatDisplay,

    updateDatesValid (newVal) {
      this.validDates = newVal
    },

    updateDobField (newDate) {
      this.adverseIndividual.dob = newDate
    },

    ...mapMutations([
      'setViewAdverseIndividualsDialog',
    ]),
  },
}
</script>
